import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const Toast = ({ 
  message, 
  type = 'info', 
  duration = 5000, 
  onClose, 
  action,
  persistent = false 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!persistent && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, persistent]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) {
        onClose();
      }
    }, 200);
  };

  const handleActionClick = () => {
    if (action?.onClick) {
      action?.onClick();
    }
    handleClose();
  };

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-success text-success-foreground border-success/20';
      case 'warning':
        return 'bg-warning text-warning-foreground border-warning/20';
      case 'error':
        return 'bg-error text-error-foreground border-error/20';
      case 'info':
      default:
        return 'bg-card text-card-foreground border-border';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return 'CheckCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'error':
        return 'XCircle';
      case 'info':
      default:
        return 'Info';
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`
        fixed top-20 left-1/2 transform -translate-x-1/2 z-toast
        max-w-md w-full mx-4 p-4 rounded-lg border shadow-warm-lg
        transition-all duration-200 ease-out
        ${getToastStyles()}
        ${isExiting ? 'opacity-0 translate-y-[-10px]' : 'opacity-100 translate-y-0'}
      `}
    >
      <div className="flex items-start space-x-3">
        <Icon name={getIcon()} size={20} className="flex-shrink-0 mt-0.5" />
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-5">{message}</p>
          
          {action && (
            <div className="mt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleActionClick}
                className="text-current hover:bg-white/10 p-1 h-auto"
              >
                {action?.label}
              </Button>
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleClose}
          className="text-current hover:bg-white/10 p-1 h-auto flex-shrink-0"
        >
          <Icon name="X" size={16} />
        </Button>
      </div>
      {/* Progress bar for timed toasts */}
      {!persistent && duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 rounded-b-lg overflow-hidden">
          <div
            className="h-full bg-white/40 transition-all ease-linear"
            style={{
              width: '100%',
              animation: `toast-progress ${duration}ms linear forwards`
            }}
          />
        </div>
      )}
      <style jsx>{`
        @keyframes toast-progress {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts = [], onRemoveToast }) => {
  return (
    <div className="fixed top-0 left-0 right-0 z-toast pointer-events-none">
      <div className="flex flex-col items-center space-y-2 pt-20">
        {toasts?.map((toast) => (
          <div key={toast?.id} className="pointer-events-auto">
            <Toast
              {...toast}
              onClose={() => onRemoveToast(toast?.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// Hook for managing toasts
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (toast) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { ...toast, id }]);
    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev?.filter(toast => toast?.id !== id));
  };

  const showSuccess = (message, options = {}) => {
    return addToast({ message, type: 'success', ...options });
  };

  const showError = (message, options = {}) => {
    return addToast({ message, type: 'error', ...options });
  };

  const showWarning = (message, options = {}) => {
    return addToast({ message, type: 'warning', ...options });
  };

  const showInfo = (message, options = {}) => {
    return addToast({ message, type: 'info', ...options });
  };

  return {
    toasts,
    addToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};

export { ToastContainer };
export default Toast;